// src/views/kiddo-games/pick/KiddoPickView.tsx
// Wybierz Easy — kopia src/views/pick/PickView.tsx, rozwijana osobno.
// Różnice: własne style i obrazek, następna gra z grupy Easy, osobne zapamiętanie przełącznika polskiego audio,
// głośnik na obrazku czytający angielskie słowo, najwyżej 4 słowa do wyboru zamiast 6.
/* eslint-disable max-len */
import React, {
  FC, useEffect, useState, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import classes from './KiddoPickView.module.less';
import FixWidthContent from '../../../components/FixWidthContent';
import withTagManager from '../../../hooks/withTagManager';
import { PictureOfWord } from './PictureOfWord';
import InfoButton from '../../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { BASE_API_URL } from '../../../services/ApiService';
import { getActiveList, getUserData } from '../../auth/slice/selectors';
import EndGameInfo from '../../../components/EndGameInfo/EndgameInfo';
import useGoToRoute from '../../../hooks/useGoToRoute';
import { Path } from '../../../constants/Path';
import useAudioPlayer from '../../../hooks/useAudioPlayer';
import { RightSideButtons } from '../../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../../components/EditTermModal/EditTermButton';
import { usePolishAudio } from '../../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../../constants/Roles';
import { cleanTextForTTS } from '../../../utils/textBracketSanitize';
import { getListWordId } from '../../../utils/gameWords';
import WordCounter from '../../../components/WordCounter';
import { EditListButton } from '../../../components/EditListButton/EditListButton';
import { FitText } from '../../../components/FitText/FitText';
import useActivityTracking from '../../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../../services/activity';

// Funkcja pomocnicza do tasowania
function shuffleArray<T>(array: T[]): T[] {
  return [...array].sort(() => 0.5 - Math.random());
}

// W Easy najwyżej 4 słowa do wyboru (w Regular jest 6): szukane i 3 inne z listy
const generateChoices = (currentWord: any, allWords: any[], currentIndex: number) => {
  const removedCurrentWord = allWords.filter((word) => word.id !== allWords[currentIndex].id);
  const uniqueByTerm = [...new Map(
    removedCurrentWord.map((word) => [word.term, word]),
  ).values()];
  const shuffled = shuffleArray(uniqueByTerm);
  const differentTerm = shuffled.filter((word) => word.term !== currentWord.term);
  const threeWords = differentTerm.slice(0, 3);

  return shuffleArray([...threeWords, currentWord]);
};

export const KiddoPickView: FC = () => {
  const activeList = useSelector(getActiveList);
  const { words } = activeList;
  const audioPlayer = useAudioPlayer();
  const goToNext = useGoToRoute(Path.KIDDO_PAIRING);

  useActivityTracking(ActivityGame.PICK_EASY);

  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio,
  } = usePolishAudio({ storageKey: 'kiddo_pick_playPolish' });

  const [stateWordsCopy, setStateWordsCopy] = useState(() => shuffleArray([...words]));
  const [wordOnScreen, setWordOnScreen] = useState<number>(0);
  const [mistakeStyle, setMistakeStyle] = useState<boolean | string>(false);
  const [correctStyle, setCorrectStyle] = useState<boolean | string>(false);
  const [listEndInfo, setListEndInfo] = useState(false);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  const [currentChoices, setCurrentChoices] = useState<any[]>([]);
  const lastWordOnScreenRef = React.useRef<number>(-1);

  const currentWord = stateWordsCopy[wordOnScreen];
  const numberOfWords = stateWordsCopy.length;
  const index = wordOnScreen + 1;
  const isLastWord = index === numberOfWords;

  useEffect(() => {
    if (wordOnScreen !== lastWordOnScreenRef.current) {
      if (currentWord) {
        setCurrentChoices(generateChoices(currentWord, stateWordsCopy, wordOnScreen));
        lastWordOnScreenRef.current = wordOnScreen;
      }
    } else {
      setCurrentChoices((prev) => prev.map((word) => {
        const updated = stateWordsCopy.find((w) => w.id === word.id);

        return updated || word;
      }));
    }
  }, [wordOnScreen, stateWordsCopy, currentWord]);

  // Funkcja dodająca słowo do powtórki
  const addWordToRepeat = useCallback(() => {
    const wordToRepeat = stateWordsCopy[wordOnScreen];
    const wordTerm = wordToRepeat.term;

    const remainingWords = stateWordsCopy.slice(wordOnScreen + 1);
    const alreadyHasRepeat = remainingWords.some((word) => word.term === wordTerm);

    if (alreadyHasRepeat) {
      return;
    }

    let needsSeparator = false;
    if (wordOnScreen < stateWordsCopy.length - 1) {
      const nextWordTerm = stateWordsCopy[wordOnScreen + 1].term;
      needsSeparator = (nextWordTerm === wordTerm);
    } else {
      needsSeparator = true;
    }

    const wordsToAdd = [];

    if (needsSeparator) {
      let randomWordObj;
      let attempts = 0;
      const maxAttempts = 50;

      do {
        let randomIndex;
        if (attempts < 30) {
          const preferredRange = Math.ceil(words.length * 0.7);
          randomIndex = Math.floor(Math.random() * preferredRange);
        } else {
          randomIndex = Math.floor(Math.random() * words.length);
        }

        randomWordObj = words[randomIndex];
        attempts += 1;
      } while (
        attempts < maxAttempts
        && randomWordObj.term === wordTerm
      );

      const randomCopy = { ...randomWordObj };
      randomCopy.index = stateWordsCopy.length + wordsToAdd.length;
      randomCopy.id = randomCopy.index.toString();
      randomCopy.listWordId = getListWordId(randomWordObj);

      wordsToAdd.push(randomCopy);
    }

    const repeatCopy = { ...wordToRepeat };
    repeatCopy.index = stateWordsCopy.length + wordsToAdd.length;
    repeatCopy.id = repeatCopy.index.toString();
    repeatCopy.listWordId = getListWordId(wordToRepeat);

    wordsToAdd.push(repeatCopy);

    setStateWordsCopy([
      ...stateWordsCopy,
      ...wordsToAdd,
    ]);
  }, [stateWordsCopy, wordOnScreen, words]);

  // Funkcja odtwarzająca sekwencję audio
  const playAudioSequence = useCallback((term: string, definition: string, onComplete?: () => void) => {
    const cleanTerm = cleanTextForTTS(term);

    if (!cleanTerm) {
      if (onComplete) onComplete();

      return;
    }

    const termUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;

    // Sprawdź czy polskie audio jest włączone
    audioPlayer.play(termUrl, () => {
      // B. Następnie odtwórz polskie tłumaczenie (załatwia to ulepszony hook)
      setTimeout(() => {
        // Zamiast budować URL i sprawdzać playPolishRef.current,
        // wywołujemy nasz ulepszony hook, który obsługuje:
        // - Czy grać (playPolish check)
        // - Czyszczenie tekstu (cleanTextForTTS)
        // - Callback po zakończeniu (onComplete logic)
        playPolishAudio(definition, () => {
          // Callback dla polskiego audio. Jeśli polskie było wyłączone,
          // ten callback i tak zostanie wywołany natychmiast przez hooka.
          if (onComplete) {
            setTimeout(onComplete, 300);
          }
        });
      }, 200);
    });
  }, [audioPlayer, playPolishAudio]);

  const handleClick = useCallback((id: any, term: any) => {
    // Po dobrej odpowiedzi lektor czyta słowo – do pokazania następnego ignorujemy kliknięcia
    if (correctStyle !== false) return;

    const isCorrect = currentWord?.term === term;

    recordAnswer(isCorrect, currentWord?.term);

    if (isCorrect && !isLastWord) {
      // Poprawny wybór - nie ostatnie słowo
      setMistakeStyle(false);
      setCorrectStyle(id);

      playAudioSequence(currentWord.term, currentWord.definition, () => {
        setWordOnScreen((prev) => prev + 1);
      });
    } else if (isCorrect && isLastWord) {
      // Poprawny wybór - ostatnie słowo
      setCorrectStyle(id);

      playAudioSequence(currentWord.term, currentWord.definition, () => {
        setListEndInfo(true);
      });
    } else {
      // Błędny wybór
      setCorrectStyle(false);
      setMistakeStyle(id);
      addWordToRepeat();

      const clickedWord = currentChoices.find((word) => word.id === id);
      if (clickedWord) {
        playAudioSequence(clickedWord.term, clickedWord.definition);
      }
    }
  }, [correctStyle, currentWord, isLastWord, playAudioSequence, addWordToRepeat, currentChoices]);

  // Głośnik w rogu obrazka – czyta angielskie słowo z obrazka, żeby maluch mógł je sobie przypomnieć; polskiego nie czyta.
  // Nie jest odpowiedzią. Po dobrej odpowiedzi nie działa – przerwałby lektora i gra nie przeszłaby do następnego słowa.
  const handleSpeakerClick = useCallback(() => {
    if (correctStyle !== false || !currentWord) return;

    const cleanTerm = cleanTextForTTS(currentWord.term);
    if (!cleanTerm) return;

    stopPolishAudio();
    audioPlayer.play(`${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`);
  }, [correctStyle, currentWord, audioPlayer, stopPolishAudio]);

  // Powtórzenie gry
  const repeatGame = useCallback(() => {
    setTimeout(() => {
      setWordOnScreen(0);
      setCorrectStyle(false);
      setMistakeStyle(false);
      setListEndInfo(false);
      setStateWordsCopy(shuffleArray([...words]));
      lastWordOnScreenRef.current = -1; // Reset ref
    }, 500);
  }, [words]);

  // Reset stylu correct przy zmianie wordOnScreen
  useEffect(() => {
    setCorrectStyle(false);
  }, [wordOnScreen]);

  return (
    <>
      <FixWidthContent className={classes.content}>
        {!listEndInfo && (
        <WordCounter
          current={wordOnScreen + 1}
          total={stateWordsCopy.length}
        />
        )}
        {/* Na telefonie trzymanym poziomo obrazek z lewej, a karty z prawej (KiddoPickView.module.less) */}
        <div className="enter">
          {!listEndInfo && (
            <div className={classes.stage}>
              <PictureOfWord
                id={currentWord?.id}
                image={currentWord?.image}
                alt={currentWord?.term}
                onSpeakerClick={handleSpeakerClick}
              />
              <div className={classes.pickContainer}>
                {/* Długi zwrot dostaje mniejszą czcionkę, żeby zmieścił się w karcie (na telefonie) */}
                {currentChoices.map((word: any) => (
                  <FitText
                    className={`${classes.picks} ${word?.id === mistakeStyle ? classes.mistake : ''} ${word?.id === correctStyle ? classes.correct : ''}`}
                    key={word.id}
                    id={word?.id}
                    onClick={() => handleClick(word.id, word?.term)}
                    // Klawiatura jak przy przycisku: Enter albo spacja, przytrzymany klawisz raz. Inaczej każdy klawisz
                    // na kafelku z fokusem (np. Tab po złym kliknięciu) byłby kolejną odpowiedzią
                    onKeyDown={(event) => {
                      if (!event.repeat && (event.key === 'Enter' || event.key === ' ')) {
                        handleClick(word.id, word?.term);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {word?.term}
                  </FitText>
                ))}
              </div>
            </div>
          )}
        </div>

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} kiddo />
        )}

        {!listEndInfo && <UnsplashCredits words={currentWord ? [currentWord] : []} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && currentWord && (
            <EditTermButton
              wordId={getListWordId(currentWord)}
              currentTerm={currentWord.term}
              currentDefinition={currentWord.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(currentWord);

                setStateWordsCopy((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
};

export default withTagManager(KiddoPickView);
